import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { Box, Skeleton, Typography } from "@mui/material";
import { ReactNode } from "react";
import styles from "./ActivityCard.module.scss";

interface ActivityCardProps {
  title: string;
  description: string;
  count: number;
  change: number;
  icon: ReactNode;
  dateRange?: string;
  isLoading: boolean;
}

/**
 * ActivityCard component renders a info card with metrics to show and percentage change
 *
 * @component
 * @param {string} title - Title of the info card
 * @param {string} description - short description of the card
 * @param {number} count - Metrics to show in the card
 * @param {number} change - percentage change of the mertics
 * @param {ReactNode} icon - Icon to display on the info card
 * @param {string} dateRange - Date range to show the month wise change if month is selected
 * @param {boolean} isLoading - Loader state for the card
 *
 * @example
 * <ActivityCard title="Active users" description="shows the count of active users" count={1000} change={4} icon={<UserIcon/>} />
 */
const ActivityCard = ({
  title,
  description,
  count,
  change,
  icon,
  dateRange,
  isLoading = false,
}: ActivityCardProps) => {
  const isPositive = change > 0;
  return (
    <Box className={styles.container}>
      <Box className={styles.iconWrapper}>{icon}</Box>
      <Box className={styles.details}>
        <Typography className={styles.title}>{title}</Typography>
        {isLoading ? (
          <Skeleton variant="text" className={styles.count} />
        ) : (
          <Typography className={styles.count}>{count}</Typography>
        )}
        <Typography className={styles.description}>{description}</Typography>
        {dateRange === "thisMonth" &&
          (isLoading ? (
            <Skeleton variant="text" className={styles.count} />
          ) : (
            <Box className={styles.changeWrapper}>
              <Typography>
                {isPositive ? (
                  <ArrowUpward
                    className={`${styles.arrowIcon} ${styles.positive}`}
                  />
                ) : (
                  <ArrowDownward
                    className={`${styles.arrowIcon} ${styles.negative}`}
                  />
                )}
              </Typography>

              <Typography
                className={`${styles.change} ${
                  isPositive ? styles.positive : styles.negative
                }`}
              >
                {Math.abs(change)} %
              </Typography>

              <Typography component="span" className={styles.thisMonth}>
                this month
              </Typography>
            </Box>
          ))}
      </Box>
    </Box>
  );
};

export default ActivityCard;
